import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { createAppTheme, type ColorMode } from './theme';

/** `auto` urmează tema sistemului de operare. */
export type ThemePreference = 'auto' | 'light' | 'dark';

const STORAGE_KEY = 'statie-meteo:theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';
/** Folosite doar dacă browserul nu raportează nicio preferință de sistem. */
const NIGHT_FROM = 21;
const NIGHT_UNTIL = 6;

interface ThemeModeContextValue {
  preference: ThemePreference;
  mode: ColorMode;
  setPreference: (next: ThemePreference) => void;
  /** Ciclează auto → zi → noapte → auto (butonul din antet). */
  cyclePreference: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'auto' || stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage poate fi blocat (private mode / iframe) — rămânem pe auto.
  }
  return 'auto';
}

function isNightAt(date: Date): boolean {
  const hour = date.getHours();
  return hour < NIGHT_UNTIL || hour >= NIGHT_FROM;
}

/**
 * Tema sistemului. Dacă browserul nu exprimă nicio preferință — rar, dar
 * posibil — cădem pe ora locală, ca în design: noapte între 21:00 și 06:00.
 */
function detectSystemMode(): ColorMode {
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    if (window.matchMedia(DARK_QUERY).matches) return 'dark';
    if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
  }
  return isNightAt(new Date()) ? 'dark' : 'light';
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [autoMode, setAutoMode] = useState<ColorMode>(detectSystemMode);

  useEffect(() => {
    if (preference !== 'auto') return;

    const sync = () => setAutoMode(detectSystemMode());
    sync();

    const media = window.matchMedia(DARK_QUERY);
    media.addEventListener('change', sync);
    // Ticul de un minut contează doar pe fallback-ul după oră.
    const id = window.setInterval(sync, 60_000);

    return () => {
      media.removeEventListener('change', sync);
      window.clearInterval(id);
    };
  }, [preference]);

  const mode: ColorMode = preference === 'auto' ? autoMode : preference;

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Preferința rămâne doar pe sesiunea curentă.
    }
  }, []);

  const cyclePreference = useCallback(() => {
    setPreference(preference === 'auto' ? 'light' : preference === 'light' ? 'dark' : 'auto');
  }, [preference, setPreference]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  // Atributele de pe <html> țin scrollbar-ul și form-controls native în ton.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.mui = mode === 'dark' ? 'night' : 'day';
    root.style.colorScheme = mode;
  }, [mode]);

  const value = useMemo<ThemeModeContextValue>(
    () => ({ preference, mode, setPreference, cyclePreference }),
    [preference, mode, setPreference, cyclePreference],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode trebuie folosit în interiorul <ThemeModeProvider>');
  return ctx;
}
